import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Button, Dropdown, Input, Loader, Message } from 'semantic-ui-react';
import moment from 'moment';

import banner from '../../images/login-banner.png';
import { FUNNEL_DASHBOARD } from '../../queries/getFunnelDashboard';
import styles from './index.module.css';

/**
 * Unlisted, password-gated install-funnel dashboard. Not linked from anywhere in the app —
 * reached only by typing the URL directly.
 *
 * The password is checked server-side (`funnelDashboard` resolver): this page never decides
 * on its own whether to show data, it just relays whatever `status` the server returns
 * (DISABLED / LOCKED / UNAUTHORISED / OK). A client-side-only gate would ship the rows to
 * anyone who read the network response regardless of what this component renders.
 */

const SESSION_KEY = 'joyya.funnelPassword';

const DAY_OPTIONS = [
  { key: '7', text: 'Last 7 days', value: 7 },
  { key: '30', text: 'Last 30 days', value: 30 },
  { key: '90', text: 'Last 90 days', value: 90 },
  { key: 'all', text: 'All time', value: 0 },
];

const PLATFORM_OPTIONS = [
  { key: 'both', text: 'Android + iOS', value: '' },
  { key: 'android', text: 'Android only', value: 'android' },
  { key: 'ios', text: 'iOS only', value: 'ios' },
];

function FunnelBars({ title, stages }: { title: string; stages: { label: string; count: number; percent: number }[] }) {
  return (
    <div className={styles.funnelCol}>
      <div className={styles.funnelColTitle}>{title}</div>
      {stages.map((stage) => (
        <div className={styles.barRow} key={stage.label}>
          <span className={styles.barLabel}>{stage.label}</span>
          <div className={styles.barTrack}>
            <div className={styles.barFill} style={{ width: `${stage.percent}%` }} />
          </div>
          <span className={styles.barValue}>{stage.count} ({stage.percent}%)</span>
        </div>
      ))}
    </div>
  );
}

function Funnel() {
  const [days, setDays] = useState<number>(30);
  const [platformFilter, setPlatformFilter] = useState<string>('');
  const [status, setStatus] = useState<'checking' | 'DISABLED' | 'LOCKED' | 'UNAUTHORISED' | 'OK'>('checking');
  const [data, setData] = useState<any>(null);
  const [passwordInput, setPasswordInput] = useState('');

  const [runQuery, { loading }] = useLazyQuery(FUNNEL_DASHBOARD, {
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      const res = result?.funnelDashboard;
      if (res == null) return;
      setStatus(res.status);
      if (res.status === 'OK') {
        setData(res.data);
      } else {
        setData(null);
        if (res.status !== 'DISABLED') {
          sessionStorage.removeItem(SESSION_KEY);
        }
      }
    },
  });

  const attempt = (password: string, daysArg: number, platformArg: string) => {
    runQuery({
      variables: { password, days: daysArg === 0 ? null : daysArg, platform: platformArg || null },
    });
  };

  // On mount: if this tab already authenticated, reuse that password; otherwise probe with an
  // empty password, which the server treats as a no-op check (never counted as a failed attempt)
  // rather than a real login attempt.
  useEffect(() => {
    const stored = sessionStorage.getItem(SESSION_KEY) || '';
    attempt(stored, days, platformFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status === 'OK') {
      const stored = sessionStorage.getItem(SESSION_KEY) || '';
      attempt(stored, days, platformFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, platformFilter]);

  const submitPassword = () => {
    sessionStorage.setItem(SESSION_KEY, passwordInput);
    attempt(passwordInput, days, platformFilter);
  };

  if (status === 'checking') {
    return (
      <div className={styles.page}>
        <div className={styles.brandBar}>
          <img src={banner} alt="Joyya" className={styles.brandLogo} />
        </div>
        <div className={styles.content}>
          <div className={styles.doc}>
            <Loader active inline="centered" />
          </div>
        </div>
      </div>
    );
  }

  if (status === 'DISABLED') {
    return (
      <div className={styles.page}>
        <div className={styles.brandBar}>
          <img src={banner} alt="Joyya" className={styles.brandLogo} />
        </div>
        <div className={styles.content}>
          <div className={styles.doc}>
            <h1 className={styles.docTitle}>Funnel dashboard</h1>
            <hr className={styles.divider} />
            <p className={styles.disabledNote}>
              This page is disabled — no FUNNEL_PASS is configured on the server.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status !== 'OK') {
    return (
      <div className={styles.page}>
        <div className={styles.brandBar}>
          <img src={banner} alt="Joyya" className={styles.brandLogo} />
        </div>
        <div className={styles.content}>
          <div className={styles.doc}>
            <h1 className={styles.docTitle}>Funnel dashboard</h1>
            <hr className={styles.divider} />
            {status === 'LOCKED' ? (
              <Message negative>Too many attempts. Try again later.</Message>
            ) : (
              <>
                <Input
                  fluid
                  type="password"
                  className={styles.field}
                  placeholder="Password"
                  value={passwordInput}
                  onChange={(_, { value }) => setPasswordInput(value)}
                  onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') submitPassword(); }}
                />
                <Button className={styles.submitBtn} fluid loading={loading} onClick={submitPassword}>
                  Enter
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.brandBar}>
        <img src={banner} alt="Joyya" className={styles.brandLogo} />
      </div>
      <div className={styles.content}>
        <div className={styles.docWide}>
          <h1 className={styles.docTitle}>Install funnel</h1>
          <hr className={styles.divider} />

          <div className={styles.toolbar}>
            <Dropdown selection options={DAY_OPTIONS} value={days} onChange={(_, { value }) => setDays(value as number)} />
            <Dropdown selection options={PLATFORM_OPTIONS} value={platformFilter} onChange={(_, { value }) => setPlatformFilter(value as string)} />
            {loading && <Loader active inline size="small" />}
          </div>

          <div className={styles.funnelRow}>
            <FunnelBars title="Android" stages={data.androidFunnel} />
            <FunnelBars title="iOS" stages={data.iosFunnel} />
          </div>
          <p className={styles.caveat}>
            "Opened store" / "Copied code" is the furthest we can see — whether Play or the App
            Store actually served the install is invisible to us. Cross-check against Play
            Console's own tester count.
          </p>

          <h2 className={styles.sectionTitle}>
            Stuck ({'>'}{2}h idle) — {data.stuckSessions.length}
          </h2>
          {data.stuckSessions.length === 0 ? (
            <p className={styles.empty}>Nobody is currently stuck. Nice.</p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Who</th>
                    <th>Community</th>
                    <th>Platform</th>
                    <th>Got to</th>
                    <th>Stuck since</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stuckSessions.map((s: any) => (
                    <tr key={s.sessionId}>
                      <td>{s.email ? <a href={`mailto:${s.email}?subject=Getting%20the%20Joyya%20app`}>{s.email}</a> : '(unknown)'}</td>
                      <td>{s.communityName || '—'}</td>
                      <td>{s.platform || '—'}</td>
                      <td>{s.reachedLabel}</td>
                      <td>{moment(s.lastEventAt).fromNow()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2 className={styles.sectionTitle}>Help requests — {data.helpRequests.length}</h2>
          {data.helpRequests.length === 0 ? (
            <p className={styles.empty}>None yet.</p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Community</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.helpRequests.map((h: any, i: number) => (
                    <tr key={`${h.email}-${i}`}>
                      <td><a href={`mailto:${h.email}?subject=Getting%20the%20Joyya%20app`}>{h.email}</a></td>
                      <td>{h.communityName || '—'}</td>
                      <td>{moment(h.createdAt).fromNow()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2 className={styles.sectionTitle}>By community</h2>
          {data.byCommunity.length === 0 ? (
            <p className={styles.empty}>No sessions carried a community code yet.</p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Community</th>
                    <th>Started</th>
                    <th>Reached store</th>
                    <th>Stuck now</th>
                    <th>Signups (approx.)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byCommunity.map((c: any) => (
                    <tr key={c.code}>
                      <td>{c.communityName || c.code}</td>
                      <td>{c.started}</td>
                      <td>{c.reachedStore}</td>
                      <td>{c.stuckCount}</td>
                      <td>{c.signups}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className={styles.caveat}>
            Signups can't be tied to a specific funnel session — there's no shared key between an
            anonymous visit and an account. This counts anyone who joined that community in the
            selected window, including people who installed the app another way.
          </p>

          <h2 className={styles.sectionTitle}>Recent activity</h2>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Event</th>
                  <th>Platform</th>
                  <th>Code</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {data.recentEvents.map((e: any, i: number) => (
                  <tr key={`${e.sessionId}-${i}`}>
                    <td>{moment(e.createdAt).fromNow()}</td>
                    <td>{e.eventType}</td>
                    <td>{e.platform || '—'}</td>
                    <td>{e.code || '—'}</td>
                    <td>{e.detail || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export { Funnel };
